import useScreenBreakpoint from "@hooks/useScreenBreakpoint";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import {
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  QUERY_STATUS,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import { getHistogramAxisFromDateRange } from "@utils/helpers/dateHelper";
import { getDateInterval } from "@utils/helpers/dashboardHelpers";

import { getSeriesNames } from "./helpers";
import { ChartGroups, ChartSeries } from "@customTypes/chart";
import { LawsuitTrackerLineChartResponse } from "@customTypes/services/responses";

type LawsuitTrackerResponse = {
  xAxis: string[];
  series: ChartSeries;
  groups?: ChartGroups;
  noData?: boolean;
};

type ParseCreatedLawsuitResponseType = {
  (
    response: LawsuitTrackerLineChartResponse,
    range: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
    isMobileScreen: boolean
  ): LawsuitTrackerResponse;
};

const parseCreatedLawsuitResponse: ParseCreatedLawsuitResponseType = (
  response,
  range,
  isMobileScreen
) => {
  const noData = Object.values(response).every((series) =>
    Array.isArray(series) ? series.length === 0 : !series
  );
  const xAxis = getHistogramAxisFromDateRange(range, isMobileScreen);

  const groups: ChartGroups = [];

  const years = xAxis.map((date) => moment(date).format("YYYY"));
  const uniqueYears = Array.from(new Set(years));

  uniqueYears.forEach((year) => {
    const cols = years.filter((y) => y === year).length;
    groups.push({ title: year, cols });
  });

  const mapResponse = (res: LawsuitTrackerLineChartResponse["created" | "ended"]) =>
    xAxis.map((date) => {
      const matchedDay = res.find((dayCount) => dayCount.firstKey === date);
      return matchedDay ? matchedDay.count : 0;
    });

  const { createdArrayName, endedArrayName } = getSeriesNames();

  const createdArray = { name: createdArrayName, data: mapResponse(response?.created ?? []) };
  const endedArray = { name: endedArrayName, data: mapResponse(response?.ended ?? []) };

  const series = [createdArray, endedArray];

  return { xAxis, series, groups, noData };
};

const useLawsuitTrackerLineChartQuery = (
  rangeFilter: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  caseTypeFilter: string[]
): [string, LawsuitTrackerResponse] => {
  const [lawsuitTrackerLineChartData, setLawsuitTrackerLineChartData] =
    useState<LawsuitTrackerResponse>({
      xAxis: [],
      series: [],
    });
  const [status, setStatus] = useState(QUERY_STATUS.loading);
  const { isMobileScreen } = useScreenBreakpoint();

  const { fromDate, toDate } = DASHBOARD_RANGE_TYPES_DICTIONARY[rangeFilter].range;
  const dateInterval = getDateInterval(rangeFilter, isMobileScreen);

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["lawsuitTrackerLineChart", { fromDate, toDate, dateInterval, caseTypeFilter }],
    /* istanbul ignore next */
    () =>
      perses
        .getLawsuitTrackerLineChartData(fromDate, toDate, dateInterval, caseTypeFilter)
        .then((res) => res),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseCreatedLawsuitResponse(data, rangeFilter, isMobileScreen);
      setLawsuitTrackerLineChartData(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [
    data,
    isSuccess,
    fromDate,
    toDate,
    isLoading,
    isFetching,
    rangeFilter,
    caseTypeFilter,
    isMobileScreen,
  ]);

  return [status, lawsuitTrackerLineChartData];
};

export default useLawsuitTrackerLineChartQuery;
